import { Grid } from "@mui/system";
import { useNavigate } from "react-router";
import { MutableRefObject, useEffect, useRef, useState } from "react";
import { isEmpty } from "lodash";
import { Box, Button, Typography } from "@mui/material";
import dayjs from "dayjs";
import { useForm } from "react-hook-form";

import {
  ButtonMinWidth,
  FaCheckCircleIcon,
  FaExclamationCircleIcon,
  HeaderTitle,
} from "../../PriorAuthWorkFlow/styles/style";
import colors from "../../../styles/colors/colors";
import fontWeight from "../../../styles/mui/fontWeight";
import {
  BenefitPlanDetails,
  CheckEligibility,
  EligibilityCheck,
  GetPatientInformationPayload,
  InfoAddress,
  MemberInfoData,
  PatientInformation,
  PatientInformationView,
  SaveInformation,
} from "../../../models/Patient";
import {
  flexAllEnd,
  flexColumn,
  flexRow,
} from "../../../styles/mui/styles/display";
import ReadOnlyField from "../../../components/FormPriorAuthReAuth/ReadOnlyField";
import {
  encryptId,
  getGender,
  getSessionStorage,
  getValidMBI,
  getValue,
  isEligible,
  isMemberEligible,
} from "../../../utils";
import { formatDate } from "../../../utils/DateTime";
import { DateFormats } from "../../../constants/Date";
import { rules } from "../../../utils/validation/Validation";
import { ApplicationRoute } from "../../../constants/ApplicationUrl";
import { getPayloadForInformation, Numbers } from "../../../constants/Referral";
import { MyAccountModel } from "../../../models/MyAccount";
import { defaultMemberInfoData } from "../../../reducers/MemberInfoData";
import { fontSize } from "../../../styles/mui/styles/font";
import { subscriberDetails, TextConstants } from "../../../constants/Patient";
import { defaultReferralInfo } from "../../../reducers/ReferralList";

type EligibilityCheckSuccessType = {
  checkEligibilityState: CheckEligibility;
  benefitPlanUidState: BenefitPlanDetails;
  loggedInUserState: MyAccountModel;
  patientInformationByIdState: PatientInformation;
  memberInfoDataState: MemberInfoData;
  informationState: PatientInformationView;
  information: (payload: SaveInformation) => void;
  saveMemberInfo: (payload: MemberInfoData) => void;
  informationErrorState: any;
  resetAPIResponse: () => void;
  eligibilityPayload: EligibilityCheck;
  referralId: number;
  patientId: number;
  providerId: number;
  getPatientInformationById: (payload: GetPatientInformationPayload) => void;
};

export const MemberInfo = ({
  checkEligibilityState,
  benefitPlanUidState,
  patientInformationByIdState,
  information,
  saveMemberInfo,
  memberInfoDataState,
  informationErrorState,
  eligibilityPayload,
  referralId,
  patientId,
  providerId,
  getPatientInformationById
}: EligibilityCheckSuccessType) => {
  const methods = useForm<any>({
    defaultValues: "",
    mode: "all",
  });

  const encryptedReferralId = referralId ? !isEmpty(getSessionStorage("samlToken")) ? encryptId(referralId) : referralId : null;
  const encryptedProviderId = providerId ? !isEmpty(getSessionStorage("samlToken")) ? encryptId(providerId) : providerId : null;

  const { handleSubmit, getValues, reset } = methods;

  const [memberInfoData, setMemberInfoData] = useState<MemberInfoData>(
    defaultMemberInfoData
  );

  const searchInputMember = useRef() as MutableRefObject<HTMLInputElement>;

  useEffect(() => {
    updateMemberInfo({
      isIngest:
        informationErrorState?.data?.status === 400
          ? true
          : memberInfoData.isIngest,
    });
  }, [informationErrorState]);

  useEffect(() => {
    if (referralId > 0) {
      getPatientInformationById({
        id: referralId,
        type: 2,
      });
    }
  }, [referralId]);

  useEffect(() => {
    if (patientInformationByIdState.patientId > 0) {
      setValuesBaseOnAddress();
    }
  }, [patientInformationByIdState]);

  const updateMemberInfo = (newData: Partial<MemberInfoData>) => {
    setMemberInfoData((prevData) => ({
      ...prevData,
      ...newData,
    }));
  };

  const navigate = useNavigate();

  const getPatientEligibilityStatus = () => {
    const status = isMemberEligible(checkEligibilityState)
      ? "Eligible"
      : "Not Eligible";
    return status;
  };
  const referralInfo = {
    ...defaultReferralInfo,
    patientId: patientId,
  };
  const callPostInformation = () => {
    if (isEligible(checkEligibilityState) === false) {
      return;
    }
    const payload = getPayloadForInformation({
      checkEligibilityState: checkEligibilityState,
      patientEligibilityStatus: getPatientEligibilityStatus(),
      eligibilityPayload: eligibilityPayload,
      patientFromMbiState: null,
      reAuthDefaultValueState: referralInfo,
      benefitPlanUidState: benefitPlanUidState,
      patientInformationByIdState: patientInformationByIdState,
    });
    if (
      !isEmpty(payload.informationReceived) &&
      payload.informationReceived.patientId > 0
    ) {
      const { subscriberSuffix, groupPlanId, relationshipTypeId } = subscriberDetails(checkEligibilityState);
      const updatedPayload = {
        ...payload,
        informationReceived: {
          ...payload.informationReceived,
          languagePreferenceValue: String(payload.informationReceived.languagePreferenceValue ?? ""),
          subscriberSuffix,
          groupPlanId,
          subscriberPrefix: getValue(checkEligibilityState, "supplementalInformation.info[0].userDefined8", ""),
          relationshipTypeId,
          subscriberFirstName: getValue(checkEligibilityState, "supplementalInformation.info[0].userDefined9") || null,
          subscriberLastName: getValue(checkEligibilityState, "supplementalInformation.info[0].userDefined10") || null,
          subscriberDateOfBirth:
            formatDate(
              getValue(
                checkEligibilityState,
                "supplementalInformation.info[0].userDefined11",
              ),
              DateFormats.YYYY_MM_DD,
              DateFormats.YYYYMMDD,
            ) || null,
          subscriberGender: getGender(getValue(checkEligibilityState, "supplementalInformation.info[0].userDefined12", "")),
        },
      };

      information(updatedPayload);
    }
  };

  const setValuesBaseOnAddress = (type?: number) => {
    const address = checkEligibilityState;

    const isElig = isEligible(address);

    const patientResidentialAddress = patientInformationByIdState.addressInfo.patientResidentialAddress;
    const residentialAddressObj: InfoAddress | undefined =
      patientResidentialAddress
        ? {
          addressLine: patientResidentialAddress.streetName1 ?? "",
          addressLine2: patientResidentialAddress.streetName2 ?? "",
          addressType: "1",
          city: patientResidentialAddress.city ?? "",
          state: patientResidentialAddress.patientState ?? "",
          zipCode: patientResidentialAddress.zipCode ?? "",
          countryCode: patientResidentialAddress.country ?? "",
          county: patientResidentialAddress.county ?? "",
        }
        : undefined;

    const patientMailingAddress = patientInformationByIdState.addressInfo.patientMailingAddress;
    const mailingAddressObj: InfoAddress | undefined =
      patientMailingAddress
        ? {
          addressLine: patientMailingAddress.streetName1 ?? "",
          addressLine2: patientMailingAddress.streetName2 ?? "",
          addressType: "3",
          city: patientMailingAddress.city ?? "",
          state: patientMailingAddress.patientState ?? "",
          zipCode: patientMailingAddress.zipCode ?? "",
          countryCode: patientMailingAddress.country ?? "",
          county: patientMailingAddress.county ?? "",
        }
        : undefined;

    const memberData = {
      dob: dayjs(getValue(eligibilityPayload, "dob")).format(
        DateFormats.MM_DD_YYYY
      ),
      gender: getValue(patientInformationByIdState, "memberInfo.gender"),
      payerId: getValue(eligibilityPayload, "payerId"),
      mbi: !isElig
        ? ""
        : getValidMBI(patientInformationByIdState.memberInfo.medicareBeneficiaryIdentifier),
      subscriberId: getValue(eligibilityPayload, "policyId"),
      lastName: getValue(eligibilityPayload, "lastName"),
      healthPlan: getValue(eligibilityPayload, "payerName").toUpperCase(),
      streetName1: getValue(patientInformationByIdState, "addressInfo.patientResidentialAddress.streetName1"),
      streetName2: getValue(patientInformationByIdState, "addressInfo.patientResidentialAddress.streetName2"),
      city: getValue(patientInformationByIdState, "addressInfo.patientResidentialAddress.city"),
      patientState: getValue(patientInformationByIdState, "addressInfo.patientResidentialAddress.patientState"),
      zipCode: getValue(patientInformationByIdState, "addressInfo.patientResidentialAddress.zipCode"),
      country: getValue(patientInformationByIdState, "addressInfo.patientResidentialAddress.country"),
      county: getValue(patientInformationByIdState, "addressInfo.patientResidentialAddress.county"),
      phoneNumber: getValue(patientInformationByIdState, "memberInfo.phoneNo"),
      memberPreferredLanguage: patientInformationByIdState.memberInfo.languageName ?? "",
      memberName: patientInformationByIdState.patientName || `${getValue(
        checkEligibilityState,
        "record.subscriberName.first"
      )} ${getValue(checkEligibilityState, "record.subscriberName.last")}`,
      residentialAddress: patientInformationByIdState.addressInfo.residentialAddress,
      mailingAddress: patientInformationByIdState.addressInfo.mailingAddress,
      residentialAddressObj: residentialAddressObj,
      mailingAddressObj: mailingAddressObj,
      isIngest: !isElig || getValue(informationErrorState, "status") === 400,
      isPatientCreation: false,
      referralId: referralId,
      groupId: getValue(checkEligibilityState, "supplementalInformation.info[0].userDefined7", null),
    };
    setMemberInfoData(memberData);

    const residential = patientInformationByIdState.addressInfo.patientResidentialAddress;
    if (residential) {
      reset({
        ...getValues(),
        street1: residential.streetName1 ?? "",
        street2: residential.streetName2 ?? "",
        city: residential.city ?? "",
        patientState: residential.patientState ?? "",
        zipcode: residential.zipCode ?? "",
        country: residential.country ?? "",
      });
    }
    if (type === 1) {
      setTimeout(() => {
        handleSubmit(() => onSubmit())();
      }, 500);
    }
  };

  useEffect(() => {
    if (
      memberInfoDataState &&
      !isEmpty(getValue(memberInfoDataState, "memberName"))
    ) {
      navigate(`${ApplicationRoute.CREATE_RE_AUTH}/${encryptedReferralId}/${encryptedProviderId}`);
    }
  }, [memberInfoDataState]);

  const submit = () => {
    setValuesBaseOnAddress(1);
  };
  const onSubmit = () => {
    if (
      isEligible(checkEligibilityState) &&
      !isEmpty(benefitPlanUidState) &&
      getValue(benefitPlanUidState, "benefitPlanUid", "").length > Numbers.ZERO
    ) {
      callPostInformation();
    }
    saveMemberInfo(memberInfoData);
  };
  return (
    <>
      <Grid size={12} m={2}>
        <Grid container>
          {checkEligibilityState && isEligible(checkEligibilityState) === false ? (
            <Grid size={12} sx={{ display: "flex" }}>
              <FaExclamationCircleIcon />
              <Box sx={HeaderTitle}>
                <Typography
                  variant="body2"
                  color={colors.fonts[20]}
                  fontWeight={fontWeight[5]}
                >
                  {TextConstants.REFERRAL_NOT_ELIGIBLE}
                </Typography>
              </Box>
            </Grid>
          ) : (<Grid size={12} sx={{ display: "flex" }}>
            <FaCheckCircleIcon />
            <Box sx={HeaderTitle}>
              <Typography
                variant="body2"
                color={colors.fonts[20]}
                fontWeight={fontWeight[4]}
                sx={{ fontSize: fontSize[12] }}
              >
                Referral with the below details has been successfully verified,
                please proceed with adding the rest of the data.
              </Typography>
            </Box>
          </Grid>)
          }
        </Grid>
        <Grid container>
          <Grid size={12} sx={flexColumn} mt={2}>
            <Grid container spacing={2}>
              <Grid size={{ xs: 8 }} sx={flexRow}>
                <Grid size={{ xs: 2.5 }}>
                  <ReadOnlyField
                    label={"Member Name:"}
                    value={memberInfoData.memberName}
                  />
                </Grid>
                <Grid size={{ xs: 2.5 }}>
                  <ReadOnlyField
                    label={"Date of birth:"}
                    value={formatDate(
                      getValue(eligibilityPayload, "dob"),
                      DateFormats.MM_DD_YYYY,
                      DateFormats.YYYY_MM_DD, true
                    )}
                  />
                </Grid>
                <Grid size={{ xs: 2.5 }}>
                  <ReadOnlyField
                    label={"Subscriber Id:"}
                    value={getValue(eligibilityPayload, "policyId")}
                  />
                </Grid>
                <Grid size={{ xs: 2.5 }}>
                  <ReadOnlyField
                    label={"Health Plan:"}
                    value={getValue(eligibilityPayload, "payerName")}
                  />
                </Grid>
              </Grid>
            </Grid>
            <Grid container spacing={2} mt={2} sx={flexRow}>
              <Grid size={{ xs: 4 }} sx={flexColumn}>
                {[
                  {
                    name: "street1",
                    label: "Street1: ",
                    helper: rules.street1,
                    searchInputRef: searchInputMember,
                    value: memberInfoData.streetName1,
                    disabled: false,
                  },
                  {
                    name: "street2",
                    label: "Street2: ",
                    disabled: false,
                    value: memberInfoData.streetName2,
                  },
                  {
                    name: "city",
                    label: "City: ",
                    helper: rules.city,
                    disabled: false,
                    value: memberInfoData.city,
                  },
                  {
                    name: "patientState",
                    label: "State: ",
                    helper: rules.state,
                    disabled: false,
                    value: memberInfoData.patientState,
                  },
                  {
                    name: "zipcode",
                    label: "Zip Code: ",
                    helper: rules.zipcode,
                    disabled: false,
                    value: memberInfoData.zipCode,
                  },
                  {
                    name: "county",
                    label: "County: ",
                    helper: rules.county,
                    disabled: false,
                    value: memberInfoData.county,
                  },
                ].map((field) => (
                  <Grid size={{ xs: 10 }} mt={2} key={field?.name}>
                    <ReadOnlyField
                      label={getValue(field, "label", "") ?? ""}
                      value={getValue(field, "value", "") ?? ""}
                    />
                  </Grid>
                ))}
              </Grid>
              <Grid size={{ xs: 4 }} sx={flexColumn}>
                <Grid size={{ xs: 8 }} mt={1}>
                  <ReadOnlyField label={"MBI:"} value={memberInfoData.mbi} />
                </Grid>
                <Grid size={{ xs: 8 }} mt={1}>
                  <ReadOnlyField
                    label={"Phone Number:"}
                    value={memberInfoData.phoneNumber}
                  />
                </Grid>
                <Grid size={{ xs: 8 }} mt={1}>
                  <ReadOnlyField
                    label={"Gender:"}
                    value={memberInfoData.gender}
                  />
                </Grid>
                <Grid size={{ xs: 8 }} mt={1}>
                  <ReadOnlyField
                    label={"Member Preferred Language:"}
                    value={memberInfoData.memberPreferredLanguage ?? ""}
                  />
                </Grid>
              </Grid>
            </Grid>
            <Grid container spacing={2} mt={2} sx={flexRow}>
              <Grid size={{ xs: 12 }} sx={flexAllEnd}>
                <Button
                  variant="contained"
                  onClick={() => submit()}
                  sx={ButtonMinWidth}
                >
                  Create Request
                </Button>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </>
  );
};